import {
  INPUT_TYPES,
  PARAM_GROUPS,
  PARAMETER_DEFAULT_VALUES,
  SCRAPER_REQUEST_PARAM,
  SCRAPING_PRODUCT,
  scrapingCSVFormat,
  ScrapingParamInputFieldConfig,
  targetConfig,
  targetScrapingParamInputFieldConfigMap,
} from '@decodo/utils';

import {
  requestCodeByLanguageMap,
  RequestLanguage,
} from '@/blocks/Scraper/constants/languages';

import { TranslationsTypes } from '@/lib/i18n/i18n.client';
import { Target } from './constants/targets';

export type ParameterValue =
  | string
  | number
  | boolean
  | number[]
  | string[]
  | null
  | undefined;

const NO_SELECTION = '';

type ConfigTypes = {
  [key in SCRAPER_REQUEST_PARAM]: {
    targetKey: string;
    condition?: (value: ParameterValue) => boolean;
    format?: (
      value: ParameterValue,
      language?: RequestLanguage
    ) => ParameterValue;
  };
};

interface BuildRequestDataParams {
  target: Target;
  params: ScrapingParamInputFieldConfig[];
  values: Record<SCRAPER_REQUEST_PARAM, ParameterValue>;
  language: RequestLanguage;
}

export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

const config: Partial<ConfigTypes> = {
  [SCRAPER_REQUEST_PARAM.URL]: {
    targetKey: SCRAPER_REQUEST_PARAM.URL,
    format: (value) => (value ? `"${value}"` : `""`),
  },
  [SCRAPER_REQUEST_PARAM.QUERY]: {
    targetKey: SCRAPER_REQUEST_PARAM.QUERY,
    format: (value) => (value ? `"${value}"` : `""`),
  },
  [SCRAPER_REQUEST_PARAM.PARSE]: {
    targetKey: SCRAPER_REQUEST_PARAM.PARSE,
    format: (value, language) =>
      language === 'Python' ? capitalize(String(value)) : value,
  },
  [SCRAPER_REQUEST_PARAM.DOMAIN]: {
    targetKey: SCRAPER_REQUEST_PARAM.DOMAIN,
    condition: (value) => value !== NO_SELECTION,
  },
  [SCRAPER_REQUEST_PARAM.LOCALE]: {
    targetKey: SCRAPER_REQUEST_PARAM.LOCALE,
    condition: (value) => value !== NO_SELECTION,
  },
  [SCRAPER_REQUEST_PARAM.GEO]: {
    targetKey: SCRAPER_REQUEST_PARAM.GEO,
    condition: (value) => value !== NO_SELECTION,
  },
  [SCRAPER_REQUEST_PARAM.GEO_CODE]: {
    targetKey: SCRAPER_REQUEST_PARAM.GEO,
  },
  [SCRAPER_REQUEST_PARAM.GEO_CODE_SELECT]: {
    targetKey: SCRAPER_REQUEST_PARAM.GEO,
  },
  [SCRAPER_REQUEST_PARAM.DEVICE_TYPE]: {
    targetKey: SCRAPER_REQUEST_PARAM.DEVICE_TYPE,
    condition: (value) => value !== NO_SELECTION,
  },
  [SCRAPER_REQUEST_PARAM.PARSER_TYPE]: {
    targetKey: SCRAPER_REQUEST_PARAM.PARSER_TYPE,
    condition: (value) => value !== NO_SELECTION,
  },
  [SCRAPER_REQUEST_PARAM.HEADLESS]: {
    targetKey: SCRAPER_REQUEST_PARAM.HEADLESS,
    condition: (value) => value === 'html',
  },
  [SCRAPER_REQUEST_PARAM.MARKDOWN]: {
    targetKey: SCRAPER_REQUEST_PARAM.MARKDOWN,
    condition: (value) => value === true,
  },
  [SCRAPER_REQUEST_PARAM.SESSION_ID]: {
    targetKey: SCRAPER_REQUEST_PARAM.SESSION_ID,
    condition: (value) => !!value,
  },
  [SCRAPER_REQUEST_PARAM.PAYLOAD]: {
    targetKey: SCRAPER_REQUEST_PARAM.PAYLOAD,
    condition: (value) => !!value,
  },
  [SCRAPER_REQUEST_PARAM.PROXY_POOL]: {
    targetKey: SCRAPER_REQUEST_PARAM.PROXY_POOL,
    condition: (value) => !!value,
  },
};

export const buildRequestData = ({
  target,
  params,
  values,
  language,
}: BuildRequestDataParams) => {
  let code = '';

  if (target) {
    code += `\n      "target": "${target.name}"`;
  }

  if (!Array.isArray(params)) {
    return '';
  }

  for (const param of params) {
    const parameterConfig = config[param.key];

    if (parameterConfig) {
      const { targetKey, condition, format } = parameterConfig;
      const paramValue = values[param.key as SCRAPER_REQUEST_PARAM];

      if (paramValue === undefined) {
        continue;
      }

      if (!condition || condition(paramValue)) {
        const formattedValue = format
          ? format(paramValue, language)
          : JSON.stringify(paramValue);

        code += `,\n      "${targetKey}": ${formattedValue}`;
      }
    }
  }

  return code;
};

const endpoint = 'https://scraper-api.decodo.com/v1/tasks';
const authHeader = 'Basic xxxxxxxxxxxxxxxx';
type GetRequestCodeParams = {
  target: Target;
  language: RequestLanguage;
  formValues: Record<string, string | boolean>;
};

export function buildRequestCode({
  target,
  language,
  formValues,
}: GetRequestCodeParams) {
  const buildCodeFn = requestCodeByLanguageMap[language];
  const params =
    targetScrapingParamInputFieldConfigMap[
      target.targetKey as keyof typeof targetScrapingParamInputFieldConfigMap
    ].filter(isValidFormParam);

  const code = buildCodeFn({
    url: endpoint,
    authorizationKey: authHeader,
    data: buildRequestData({
      target,
      params,
      values: formValues,
      language,
    }),
  });

  return code;
}

export const getPrettyProductLabel = (target: Target) => {
  for (const key in SCRAPING_PRODUCT) {
    const productName = SCRAPING_PRODUCT[key as keyof typeof SCRAPING_PRODUCT];
    if (productName === target.product) {
      return key;
    }
  }
};

const paramDefaults: Record<string, string | boolean> = {
  query: PARAMETER_DEFAULT_VALUES.DEFAULT_QUERY[0],
  device_type: NO_SELECTION,
  domain: 'com',
  locale: 'en-us',
  parse: true,
  markdown: false,
  proxy_pool: 'premium',
};

export function convertParameterValueToString(val: ParameterValue) {
  if (val === undefined || val === null) return '';
  if (Array.isArray(val)) return String(val[0]);
  return String(val);
}

export function initFormValuesForTarget(
  target: Target
): Record<string, string | boolean> {
  const params = targetScrapingParamInputFieldConfigMap[target.targetKey];
  const config = targetConfig[target.targetKey];
  const values: Record<string, string | boolean> = {};

  if (!Array.isArray(params)) {
    return {};
  }

  for (const param of params) {
    values[param.key] = convertParameterValueToString(
      config.defaults[param.key] ?? paramDefaults[param.key]
    );
  }

  if (config.format === 'json') {
    values['parse'] = true;
  }

  return {
    ...values,
    markdown: false,
    // headless: html maps to JS Rendering = true. Empty string (or any falsy value) maps to JS Rendering = false.
    headless: 'html',
  };
}

export function initFormValidatorForTarget(
  target: Target,
  texts: TranslationsTypes
) {
  const params = targetScrapingParamInputFieldConfigMap[target.targetKey];

  if (!Array.isArray(params)) {
    return {};
  }

  const validators: Record<string, (value: string | boolean) => string> = {};

  for (const param of params) {
    if (param.group !== 'primary') continue;
    if (param.key === 'url') {
      validators[param.key] = (value: string | boolean) => {
        if (typeof value === 'boolean') return '';
        if (!/^https?:\/\//.test(value))
          return texts.scraper.error.url_must_contain_http_or_https;
        if (URL.canParse(value)) return '';
        return texts.scraper.error.enter_a_valid_url;
      };
    } else {
      validators[param.key] = (value: string | boolean) => {
        if (typeof value === 'boolean') return '';
        return value === '' ? texts.scraper.error.required : '';
      };
    }
  }

  return validators;
}

export const getStringifiedCopyPayload = (content: unknown): string => {
  if (!content) return '';
  if (typeof content === 'object') {
    try {
      return JSON.stringify(content, null, 2);
    } catch (error) {
      return String(content);
    }
  }

  return String(content);
};

export const getTabularData = ({
  target,
  json,
}: {
  target: Target;
  json: Record<string, unknown>;
}) => {
  const config = scrapingCSVFormat.get(target.targetKey);

  const jsonTruthy = Object.keys(json).length > 0;

  return {
    columns: config?.columns || [],
    data: jsonTruthy && config?.selectData ? config?.selectData(json) : [{}],
    rowAccessor: config?.rowAccessor || undefined,
  };
};

export const getTagsFromTargets = (targets: Target[]) =>
  targets.reduce((acc, target) => {
    if (target.group && !acc.includes(target.group)) {
      acc.push(target.group);
    }
    return acc;
  }, [] as string[]);

export const isValidFormParam = (param: ScrapingParamInputFieldConfig) => {
  return (
    param.group === PARAM_GROUPS.PRIMARY ||
    param.group === PARAM_GROUPS.REGULAR ||
    param.key === SCRAPER_REQUEST_PARAM.PARSE ||
    param.key === SCRAPER_REQUEST_PARAM.HEADLESS ||
    param.key === SCRAPER_REQUEST_PARAM.MARKDOWN ||
    param.key === SCRAPER_REQUEST_PARAM.PROXY_POOL
  );
};

export const isPrimaryParam = (param: ScrapingParamInputFieldConfig) =>
  param.group === PARAM_GROUPS.PRIMARY;

export const isAdditionalParam = (param: ScrapingParamInputFieldConfig) =>
  param.group === PARAM_GROUPS.REGULAR;

export const isTextSelectParam = (param: ScrapingParamInputFieldConfig) =>
  param.type === INPUT_TYPES.SELECT || param.type === INPUT_TYPES.TEXT;

export const isCheckboxParam = (param: ScrapingParamInputFieldConfig) =>
  param.key === SCRAPER_REQUEST_PARAM.MARKDOWN ||
  param.key === SCRAPER_REQUEST_PARAM.HEADLESS;
